import type { ReactNode } from "react";
import { ToggleButton, TooltipTrigger } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { cva } from "../../../../../styled-system/css";
import { Tooltip } from "../../../../components/core/basic/Tooltip";

interface DaypartingCellProps {
  hour: number;
  day: number;
  bidModifier: number;
  color: string;
  isSelected: boolean;
  isEnabled: boolean;
  onToggle: (hour: number, day: number, shiftKey: boolean) => void;
  onPointerDown?: (hour: number, day: number, shiftKey: boolean) => void;
  onPointerEnter?: (hour: number, day: number) => void;
  formattedValue?: ReactNode;
  metricName?: string;
  dayName?: string;
  hourLabel?: string;
}

const cellRecipe = cva({
  base: {
    all: "unset",
    boxSizing: "border-box",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "light",
    minHeight: "48px",
    fontSize: "xsmall",
    fontWeight: "500",
    transition: "all 0.15s ease",
    _focusVisible: {
      outline: "2px solid {colors.bootstrapBlue}",
    },
    _hover: {
      boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.3)",
    },
    "&[data-selected]": {
      boxShadow: "inset 0 0 0 2px {colors.trellisPalette.3}",
    },
    "&[data-selected]:hover": {
      boxShadow: "inset 0 0 0 2px {colors.trellisPalette.3}, 0 0 0 2px rgba(0, 0, 0, 0.3)",
    },
  },
  variants: {
    isDisabled: {
      true: {
        cursor: "default",
      },
    },
  },
});

export function DaypartingCell({
  hour,
  day,
  bidModifier,
  color,
  isSelected,
  isEnabled,
  onToggle,
  onPointerDown,
  onPointerEnter,
  formattedValue,
  metricName,
  dayName,
  hourLabel,
}: DaypartingCellProps) {
  const { t } = useTranslation(["advertising"]);
  const hasTooltipData = formattedValue && metricName && dayName && hourLabel;

  // Don't use isDisabled prop - it prevents hover events for tooltips.
  // Instead, handle disabled state manually via onPress/onPointerDown guards.
  const button = (
    <ToggleButton
      isSelected={isSelected}
      style={{ backgroundColor: color }}
      className={cellRecipe({ isDisabled: !isEnabled })}
      onPress={(e) => {
        if (isEnabled) {
          onToggle(hour, day, e.shiftKey);
        }
      }}
      onPointerDown={(e) => {
        if (isEnabled && e.button === 0) {
          onPointerDown?.(hour, day, e.shiftKey);
        }
      }}
      onPointerEnter={() => {
        if (isEnabled) {
          onPointerEnter?.(hour, day);
        }
      }}
    >
      {isEnabled && <span>{`${bidModifier}x`}</span>}
    </ToggleButton>
  );

  if (!hasTooltipData) {
    return button;
  }

  return (
    <TooltipTrigger delay={300} closeDelay={0}>
      {button}
      <Tooltip placement="top" offset={8}>
        <div>
          {metricName}: {formattedValue}
        </div>
        <div>{t(($) => $.advertising["{hourLabel} on {dayName}s"], { hourLabel, dayName })}</div>
        {isEnabled && bidModifier !== 1 && (
          <div>{t(($) => $.advertising["Bid Modifier: {bidModifier}x"], { bidModifier })}</div>
        )}
      </Tooltip>
    </TooltipTrigger>
  );
}
